import l from "components/util/l";
import { readAccountFor } from "components/presentation/sync/SyncFlow.lib.session";

// The Workix server a lite installation syncs from. The browser talks to it directly, the
// same calls the VS Code extension makes: its API answers every origin, so only the sync
// itself goes through this installation's server, which is where the import has to run.

// The extension's presets, production first: a client syncs from it.
const SERVERS = [
  { label: l("Producción%EN015"), apiUrl: "https://internal-api.workix.mx" },
  { label: "Test 1", apiUrl: "https://test-api-1.workix.mx" },
  { label: "Test 2", apiUrl: "https://test-api-2.workix.mx" },
  { label: "Test 3", apiUrl: "https://test-api-3.workix.mx" },
  { label: "Local", apiUrl: "http://localhost:8008" },
];

// Every environment the account reaches, so a project it was invited to only in DEV
// still shows up; each family is listed once, named after its root.
const PROJECTS_QUERY = `{
  flows(query: "is_temp=false AND is_active=true", orderBy: "name") {
    id
    name
    environment
    environmentRoot { id name }
  }
}`;

const ENVIRONMENT_ORDER = ["PROD", "DEV"];

// A preset is named; any other server reads as its host.
const getServerLabel = (apiUrl) => {
  const server = SERVERS.find((preset) => preset.apiUrl == apiUrl);
  return server ? server.label : new URL(apiUrl).host;
};

const signIn = async (apiUrl, email, password) => {
  const response = await fetch(`${apiUrl}/api/auth/login/`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ email, password }),
  });
  if (!response.ok) throw response;
  return (await response.json()).key;
};

const fetchProjects = async (session) => {
  const response = await requestSource(session, "/graphql/", {
    method: "POST",
    body: JSON.stringify({ query: PROJECTS_QUERY }),
  });
  return groupProjects(response.data.flows);
};

const fetchEnvironments = async (session, projectFlowId) => {
  const environments = await requestSource(session, `/api/flows/environments/?flow_id=${projectFlowId}`);
  return environments.sort((first, second) => environmentRank(first) - environmentRank(second));
};

// The synced flows whose source has moved on since their last sync. Only a flow whose server
// has a saved account can be checked, and a check that fails leaves the flow out: a flow is
// marked only for a change that could be confirmed.
const findFlowsWithUpdates = async (flows) => {
  const checks = flows
    .filter((flow) => flow.sync && readAccountFor(flow.sync.api_url))
    .map(async (flow) => {
      const fingerprint = await fetchFingerprint(readAccountFor(flow.sync.api_url), flow.sync.flow_id)
        .catch(() => flow.sync.fingerprint);
      return fingerprint != flow.sync.fingerprint ? flow.id : null;
    });
  return (await Promise.all(checks)).filter((flowId) => flowId != null);
};

const fetchFingerprint = async (account, flowId) =>
  (await requestSource(account, `/api/flows/sync_fingerprint/?flow_id=${flowId}`)).fingerprint;

// This installation's /flows/sync answers 502 when the source refused it, carrying the
// source's own status: a 401 there means the source session is gone, not this one.
const isSourceSignedOut = async (response) =>
  response.status == 502 && (await response.json()).source_status == 401;

const requestSource = async (session, path, options = {}) => {
  const response = await fetch(session.apiUrl + path, {
    ...options,
    headers: { "Content-Type": "application/json", "Authorization": `Token ${session.token}` },
  });
  if (!response.ok) throw response;
  return response.json();
};

// A flow whose root was cleared stands alone as its own project.
const groupProjects = (flows) => {
  const projects = {};
  for (const flow of flows.filter((flow) => flow.environment != "TEST")) {
    const root = flow.environmentRoot || flow;
    if (!projects[root.id] || flow.id == root.id)
      projects[root.id] = { id: flow.id, name: root.name };
  }
  return Object.values(projects).sort((first, second) => first.name.localeCompare(second.name));
};

const environmentRank = (environment) => {
  const rank = ENVIRONMENT_ORDER.indexOf(environment.environment);
  return rank == -1 ? ENVIRONMENT_ORDER.length : rank;
};

export { SERVERS, getServerLabel, signIn, fetchProjects, fetchEnvironments, findFlowsWithUpdates, isSourceSignedOut };
